// visitTimes.js — read-side helpers for GPS auto-tracked "time on site" analytics.
//
// The field app writes one client_history row per completed GPS visit
// (event_type='time_on_site', new_value=dwell seconds, old_value=technician_id).
// The DB view `client_visit_times` (security_invoker, org-RLS enforced) exposes those
// as typed columns: {id, client_id, technician_id, seconds, created_at}.
//
// These helpers aggregate that view into the "typical clean time" surfaced on the
// client + technician profiles and the daily route. Independent visits stay listable;
// averages are derived on read (no dedicated table).

import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "./supabase";

const H = () => ({ apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` });

async function getRows(query) {
  try {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/client_visit_times?${query}`, { headers: H() });
    const rows = await r.json();
    return Array.isArray(rows) ? rows : [];
  } catch { return []; }
}

const avgMin = (secsArr) =>
  secsArr.length ? Math.round(secsArr.reduce((a, b) => a + b, 0) / secsArr.length / 60) : null;

// "~18 min" / "" — shared formatter so every surface reads identically.
export const fmtClean = (min) => (min == null ? "" : `~${min} min`);

// ── Per-client ────────────────────────────────────────────────────────────────
export async function fetchClientVisits(clientId) {
  return getRows(`client_id=eq.${clientId}&select=technician_id,seconds,created_at&order=created_at.desc`);
}

// Overall avg + per-tech breakdown ("By: Ruben — ~18 min (6)").
export function summarizeClientVisits(rows, technicians = []) {
  const nameOf = (id) => (technicians.find((t) => t.id === id)?.name) || (id ? "Unknown tech" : "Unattributed");
  const secs = rows.map((r) => r.seconds).filter(Number.isFinite);
  const byTechMap = {};
  rows.forEach((r) => { const k = r.technician_id || "__none__"; (byTechMap[k] ||= []).push(r.seconds); });
  const byTech = Object.entries(byTechMap)
    .map(([id, arr]) => ({
      technician_id: id === "__none__" ? null : id,
      technician: nameOf(id === "__none__" ? null : id),
      avgMin: avgMin(arr),
      visits: arr.length,
    }))
    .sort((a, b) => b.visits - a.visits);
  return {
    avgMin: avgMin(secs),
    visits: secs.length,
    byTech,
    distinctTechs: byTech.filter((b) => b.technician_id).length, // real (attributed) techs only
  };
}

// ── Per-technician ──────────────────────────────────────────────────────────────
export async function fetchTechVisits(techId) {
  return getRows(`technician_id=eq.${techId}&select=client_id,seconds,created_at&order=created_at.desc`);
}

export function summarizeTechVisits(rows, clients = []) {
  const nameOf = (id) => (clients.find((c) => c.id === id)?.name) || "Unknown client";
  const secs = rows.map((r) => r.seconds).filter(Number.isFinite);
  const g = {};
  rows.forEach((r) => { (g[r.client_id] ||= []).push(r); });
  const perClient = Object.entries(g)
    .map(([cid, arr]) => ({
      client_id: cid,
      name: nameOf(cid),
      avgMin: avgMin(arr.map((r) => r.seconds)),
      visits: arr.length,
      lastVisit: arr.reduce((mx, r) => (r.created_at > mx ? r.created_at : mx), arr[0].created_at),
    }))
    // FASTEST FIRST. The tech profile is read to answer "where does the time go?",
    // so the quick stops belong at the top and the time sinks fall to the bottom
    // where they're obvious. Clients with no timed visit yet (avgMin null) sort
    // last rather than leading the table with blanks.
    .sort((a, b) => {
      if (a.avgMin == null && b.avgMin == null) return (b.lastVisit || "").localeCompare(a.lastVisit || "");
      if (a.avgMin == null) return 1;
      if (b.avgMin == null) return -1;
      return a.avgMin - b.avgMin;
    });
  const allVisits = rows.map((r) => ({
    created_at: r.created_at,
    client_id: r.client_id,
    name: nameOf(r.client_id),
    minutes: Math.round(r.seconds / 60),
  }));
  return { overallAvgMin: avgMin(secs), totalVisits: secs.length, perClient, allVisits };
}

// ── Org-wide map for the daily-route lists: {client_id: {avgMin, visits}} in one fetch.
export async function fetchClientAvgMap() {
  const rows = await getRows(`select=client_id,seconds`);
  const g = {};
  rows.forEach((x) => { if (Number.isFinite(x.seconds)) (g[x.client_id] ||= []).push(x.seconds); });
  const out = {};
  Object.entries(g).forEach(([cid, arr]) => { out[cid] = { avgMin: avgMin(arr), visits: arr.length }; });
  return out;
}
